import type { ResourceTableDescriptor, TranslateFn } from '@/components/ui/embedded-resource-table/types';
import type { ToolbarFilterOption } from '@/components/ui/generic-toolbar/GenericToolbar';
import { buildAssignedUserOptions, type AssignableUser } from '@/lib/assigned-user-options';
import { settingLabel } from '@/lib/status-labels';
import type { CalculationRequest } from '../types';

function personName(person: { first_name?: string | null; last_name?: string | null } | null | undefined): string {
    return person ? [person.first_name, person.last_name].filter(Boolean).join(' ').trim() : '';
}

/**
 * The one description of the calculation queue, read by
 * `pages/calculation-requests/index.tsx`, by the employee profile's calculations
 * tab, and by CalculationRequestTable itself.
 */
export const calculationRequestTableDescriptor: ResourceTableDescriptor<CalculationRequest> = {
    key: 'calculation-requests',
    resourceName: 'calculation_requests',
    resourceNamePlural: 'calculation_requests',
    getRowId: (request) => request.id,

    columns: [
        {
            id: 'request_number',
            labelKey: 'calculation_requests.tables.request_number',
            labelFallback: 'Number',
            sort: { serverKey: 'request_number', value: (r) => r.request_number ?? '' },
        },
        {
            id: 'object_name',
            labelKey: 'calculation_requests.tables.object_name',
            labelFallback: 'Object',
            sort: { serverKey: 'object_name', value: (r) => r.object_name ?? '' },
        },
        { id: 'company', labelKey: 'calculation_requests.tables.company', labelFallback: 'Customer' },
        { id: 'project_phase', labelKey: 'calculation_requests.tables.project_phase', labelFallback: 'Phase' },
        {
            id: 'status',
            labelKey: 'calculation_requests.tables.status',
            labelFallback: 'Status',
            sort: { serverKey: 'status', value: (r) => String(r.status ?? '') },
        },
        { id: 'assignee', labelKey: 'calculation_requests.tables.assignee', labelFallback: 'Engineer' },
        { id: 'submitted_by', labelKey: 'calculation_requests.tables.submitted_by', labelFallback: 'Created by' },
        {
            id: 'requested_calculation_date',
            labelKey: 'calculation_requests.tables.requested_calculation_date',
            labelFallback: 'Needed by',
            sort: { serverKey: 'requested_calculation_date', value: (r) => r.requested_calculation_date ?? '' },
        },
        {
            id: 'submitted_at',
            labelKey: 'calculation_requests.tables.submitted_at',
            labelFallback: 'Submitted',
            sort: { serverKey: 'submitted_at', value: (r) => r.submitted_at ?? '' },
        },
        {
            id: 'completed_at',
            labelKey: 'calculation_requests.tables.completed_at',
            labelFallback: 'Completed',
            sort: { serverKey: 'completed_at', value: (r) => r.completed_at ?? '' },
        },
        { id: 'complexity', labelKey: 'calculation_requests.tables.complexity', labelFallback: 'Complexity' },
        {
            id: 'hours_spent',
            labelKey: 'calculation_requests.tables.hours_spent',
            labelFallback: 'Hours',
            sort: { serverKey: 'hours_spent', value: (r) => Number(r.hours_spent ?? 0) },
        },
        { id: 'turnaround', labelKey: 'calculation_requests.tables.turnaround', labelFallback: 'Turnaround' },
        { id: 'calculated_amount', labelKey: 'calculation_requests.tables.calculated_amount', labelFallback: 'Calculated' },
        {
            id: 'sold_amount',
            labelKey: 'calculation_requests.tables.sold_amount',
            labelFallback: 'Sold',
            sort: { serverKey: 'sold_amount', value: (r) => Number(r.sold_amount ?? 0) },
        },
        { id: 'outcome', labelKey: 'calculation_requests.tables.outcome', labelFallback: 'Outcome' },
        { id: 'lead', labelKey: 'calculation_requests.tables.lead', labelFallback: 'Lead' },
        { id: 'project', labelKey: 'calculation_requests.tables.project', labelFallback: 'Project' },
        {
            id: 'created_at',
            labelKey: 'calculation_requests.tables.created_at',
            labelFallback: 'Created',
            sort: { serverKey: 'created_at', value: (r) => r.created_at ?? '' },
        },
    ],

    /**
     * The queue's working set. Complexity and outcome are filterable but off by
     * default; EmbeddedResourceTable and the index page both force a column back
     * into view while its own filter is active, so narrowing by one always shows
     * what you narrowed by.
     */
    defaultVisibleColumns: ['request_number', 'object_name', 'company', 'project_phase', 'status', 'assignee', 'requested_calculation_date'],

    filters: [
        {
            id: 'status',
            param: 'status',
            labelKey: 'calculation_requests.index.quick_filters.status',
            labelFallback: 'Status',
            matches: (r, values) => values.includes(String(r.status ?? '')),
        },
        {
            id: 'assignee',
            param: 'assigned_to_user_id',
            labelKey: 'calculation_requests.index.quick_filters.assignee',
            labelFallback: 'Engineer',
            // 'unassigned' is a pseudo-value the server understands, and the
            // unassigned queue is the filter people reach for most.
            matches: (r, values) => (r.assigned_to_user_id ? values.includes(r.assigned_to_user_id) : values.includes('unassigned')),
        },
        {
            id: 'outcome',
            param: 'outcome',
            labelKey: 'calculation_requests.index.quick_filters.outcome',
            labelFallback: 'Outcome',
            matches: (r, values) => values.includes(String(r.outcome ?? '')),
        },
        {
            id: 'project_phase',
            param: 'project_phase',
            labelKey: 'calculation_requests.tables.project_phase',
            labelFallback: 'Phase',
            matches: (r, values) => values.includes(String(r.project_phase ?? '')),
        },
        {
            id: 'complexity',
            param: 'complexity',
            labelKey: 'calculation_requests.tables.complexity',
            labelFallback: 'Complexity',
            matches: (r, values) => values.includes(String(r.complexity ?? '')),
        },
        {
            // Whoever raised the calculation — usually sales, not an engineer.
            id: 'submitted_by',
            param: 'submitted_by_user_id',
            labelKey: 'calculation_requests.tables.submitted_by',
            labelFallback: 'Created by',
            matches: (r, values) => values.includes(String(r.submitted_by?.id ?? '')),
        },
        {
            id: 'company',
            param: 'company_id',
            labelKey: 'calculation_requests.tables.company',
            labelFallback: 'Customer',
            matches: (r, values) => values.includes(String(r.company?.id ?? '')),
        },
    ],

    quickFilterIds: ['status', 'assignee', 'outcome'],

    searchValues: (r) => [r.request_number, r.object_name, r.company?.name, personName(r.assignee), personName(r.submitted_by)],

    allLabel: { labelKey: 'calculation_requests.index.toolbar.quick_filters', labelFallback: 'Quick filters' },
    quickFiltersLabel: { labelKey: 'calculation_requests.index.toolbar.quick_filters', labelFallback: 'Quick filters' },
};

export interface CalculationRequestFilterOptionSources {
    t: TranslateFn;
    statuses: Array<{ value: string; label?: string }>;
    phases: Array<{ value: string; label?: string }>;
    complexities: Array<{ value: string; label?: string }>;
    outcomes: string[];
    /** Only engineers can hold a calculation, so this seat has a closed list. */
    engineers: AssignableUser[];
    /** Everyone — a calculation is usually raised by sales, who are not engineers. */
    submitters: AssignableUser[];
    companies: Array<{ id: string; name: string }>;
}

export function calculationRequestFilterOptions({
    t,
    statuses,
    phases,
    complexities,
    outcomes,
    engineers,
    submitters,
    companies,
}: CalculationRequestFilterOptionSources): Record<string, ToolbarFilterOption[]> {
    return {
        status: statuses.map((status) => ({
            value: status.value,
            label: settingLabel(t, 'calculation_request_statuses', status.value) || status.label || status.value,
        })),
        project_phase: phases.map((phase) => ({
            value: phase.value,
            label: settingLabel(t, 'calculation_request_phases', phase.value) || phase.label || phase.value,
        })),
        complexity: complexities.map((complexity) => ({
            value: complexity.value,
            label: settingLabel(t, 'calculation_request_complexities', complexity.value) || complexity.label || complexity.value,
        })),
        outcome: outcomes.map((outcome) => ({ value: outcome, label: t(`calculation_requests.outcomes.${outcome}`, outcome) })),
        assignee: [
            { value: 'unassigned', label: t('calculation_requests.index.filters.unassigned', 'Unassigned') },
            ...buildAssignedUserOptions(engineers, t),
        ],
        // Grouped like every other people filter. This used to draw its labels
        // from the engineer list, so a request raised by a sales manager had no
        // name to render at all.
        submitted_by: buildAssignedUserOptions(submitters, t),
        company: companies.map((company) => ({ value: company.id, label: company.name })).sort((a, b) => a.label.localeCompare(b.label)),
    };
}
